import { type IceSnapshot, levelById, levelNumber } from '@xox/shared'
import { Play } from 'lucide-react'
import type { LineupEntry } from '../components/race/Lineup'
import { type PodiumEntry, RoundResults } from '../components/race/RoundResults'
import { hostNameOf, WaitingPill } from '../components/race/WaitingPill'
import { Button } from '../components/ui'
import { ordinal, plural } from '../lib/format'
import { Skater } from './art/Skater'
import { iceEntries, LevelStepper } from './RaceLobby'

interface Props {
  snap: IceSnapshot
  playerId: string | null
  pickedLevel: string
  onPickLevel: (id: string) => void
  onNext: () => void
}

const podiumSkater = (e: LineupEntry, place: number) => (
  <Skater
    color={e.color}
    mood={place === 1 ? 'finished' : 'idle'}
    className={place === 1 ? 'h-11' : 'h-9'}
  />
)

export function RaceResults({ snap, playerId, pickedLevel, onPickLevel, onNext }: Props) {
  const entries = iceEntries(snap.racers, snap.hostId, playerId)
  const byId = new Map(entries.map((e) => [e.id, e]))
  const podium: PodiumEntry[] = snap.racers
    .filter((r) => r.place !== null)
    .map((r) => ({
      place: r.place as number,
      entry: byId.get(r.profile.id)!,
      detail: `${plural(r.moves, 'move')} · ${snap.scores[r.profile.id] ?? 0} pts`,
    }))
  const winner = snap.racers.find((r) => r.place === 1)
  const mine = snap.racers.find((r) => r.profile.id === playerId)
  const isHost = snap.hostId === playerId
  const unfinished = snap.racers.filter((r) => r.place === null)
  const picked = levelById(pickedLevel)

  return (
    <RoundResults
      eyebrow={`Round ${snap.round} results`}
      title={
        mine?.place === 1
          ? 'You won the round!'
          : winner
            ? `${winner.profile.nickname} wins the round`
            : 'Nobody reached the door'
      }
      subtitle={
        mine && mine.place !== 1
          ? mine.place
            ? `You finished ${ordinal(mine.place)}`
            : 'You did not finish'
          : null
      }
      podium={winner ? podium : []}
      figure={podiumSkater}
      confetti={!!winner}
      note={
        unfinished.length > 0
          ? `Still on the ice: ${unfinished.map((r) => r.profile.nickname).join(', ')}`
          : null
      }
      footer={
        isHost ? (
          <div className="flex w-full flex-col gap-3">
            <Button
              variant="arcade"
              tone="go"
              className="w-full"
              onClick={onNext}
              aria-label={`Next round: #${levelNumber(pickedLevel)} ${picked?.name ?? ''}`}
            >
              <Play className="size-5" aria-hidden /> Next round
            </Button>
            <LevelStepper levelId={pickedLevel} onChange={onPickLevel} editable />
          </div>
        ) : (
          <WaitingPill>
            {`Waiting for ${hostNameOf(entries, snap.hostId)} to start the next round…`}
          </WaitingPill>
        )
      }
    />
  )
}
